import { useEffect, useMemo, useRef, useState } from "react";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  dropTargetForExternal,
  monitorForExternal,
} from "@atlaskit/pragmatic-drag-and-drop/external/adapter";
import { containsFiles } from "@atlaskit/pragmatic-drag-and-drop/external/file";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";
import { useGetDataRoomFile } from "@vega-api/generated/coinvest-deal-data-room-files-gp/coinvest-deal-data-room-files-gp";
import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { UnsavedChangesModal } from "@vega-core/components/Modals/UnsavedChangesModal/UnsavedChangesModal";
import { PDFViewer } from "@vega-core/components/PDFViewer/PDFViewer";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useNavigationBlocker } from "@vega-core/hooks/useNavigationBlocker";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { isViewableInApp } from "@vega-modules/scenes/CoInvestments/utils/dataRoomUtils";
import { openDataRoomFile } from "@vega-modules/scenes/CoInvestments/utils/openDataRoomFile";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { useDataRoomData } from "../../hooks/useDataRoomData";
import { useDataRoomPermissions } from "../../hooks/useDataRoomPermissions";
import { useDataRoomUpload } from "../../hooks/useDataRoomUpload";

import { useColumnResize } from "./hooks/useColumnResize";
import { useLpColumnResize } from "./hooks/useLpColumnResize";
import { useScrollFrozenColumnFocus } from "./hooks/useScrollFrozenColumnFocus";
import { buildAddedNodeSummaries } from "./utils/treeUtils";
import { AddDocumentModal } from "./AddDocumentModal";
import { LP_COLUMN_MIN_WIDTH } from "./constants";
import { CreateFolderModal } from "./CreateFolderModal";
import { DataRoomDropZone } from "./DataRoomDropZone";
import { DataRoomLpAccessCell } from "./DataRoomLpAccessCell";
import {
  ALL_COLUMN_BORDER_RIGHT_WIDTH,
  DataRoomRowBulkActions,
  DOWNLOAD_COLUMN_WIDTH,
} from "./DataRoomRowBulkActions";
import { FileTreeRow } from "./FileTreeRow";
import { LpColumnHeader } from "./LpColumnHeader";
import { NodeActionsMenu } from "./NodeActionsMenu";
import { PendingChangesModal } from "./PendingChangesModal";
import { ReplaceFilesModal } from "./ReplaceFilesModal";
import type { NodeMap, TreeNode } from "./types";
import { UnsavedChangesBar } from "./UnsavedChangesBar";
import { UploadProgressPanel } from "./UploadProgressPanel";

const TREE_COLUMN_INITIAL_WIDTH = 280;
const ALL_COLUMN_WIDTH = 44;
const LP_COLUMN_INITIAL_WIDTH = 104;

function resolveParentId(
  targetElement: Element,
  rootDropEl: Element,
  nodeMap: NodeMap,
): string | null {
  if (targetElement === rootDropEl) return null;
  const nodeId = targetElement.getAttribute("data-node-id");

  if (!nodeId) return null;
  const node = nodeMap[nodeId];

  return node?.type === "folder" ? nodeId : (node?.parentId ?? null);
}

function getPendingNodes<TNode extends { id: string; path: string }>(
  nodeIds: Iterable<string>,
  nodeMap: NodeMap,
  mapNode: (node: TreeNode) => TNode,
): TNode[] {
  const pendingNodes: TNode[] = [];

  for (const nodeId of nodeIds) {
    const node = nodeMap[nodeId];

    if (!node) continue;

    pendingNodes.push(mapNode(node));
  }

  return pendingNodes;
}

type DataRoomPermissionsProps = {
  className?: string;
  dealId: string;
};

export function DataRoomPermissions({
  className,
  dealId,
}: DataRoomPermissionsProps) {
  // Closed deals keep the data room viewable (view + download) but lock all mutations
  const { isMutationLocked } = useCoInvestDealReadOnly();
  const { dataRoomId, dealOpportunities, parsedData, isLoading, isError } =
    useDataRoomData(dealId);
  const permissions = useDataRoomPermissions(
    dealId,
    dataRoomId,
    parsedData,
    dealOpportunities,
  );
  const hasChanges = permissions.changeCount > 0;
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const navigationBlocker = useNavigationBlocker(hasChanges);
  const {
    value: isConfirmSaveOpen,
    setTrue: openConfirmSave,
    setFalse: closeConfirmSave,
  } = useBoolean(false);
  const [modalParentId, setModalParentId] = useState<string | null>(null);
  const {
    value: isCreateFolderOpen,
    setTrue: openCreateFolder,
    setFalse: closeCreateFolder,
  } = useBoolean(false);
  const treeColumn = useColumnResize(
    TREE_COLUMN_INITIAL_WIDTH,
    "[data-testid='data-room-permissions']",
  );
  const lpColumns = useLpColumnResize(
    dealOpportunities.map((dealOpportunity) => dealOpportunity.id),
    LP_COLUMN_INITIAL_WIDTH,
  );
  const upload = useDataRoomUpload({
    dealId,
    dataRoomId,
    getNodeMap: () => {
      const { nodeMap, deletedIds } = permissions;

      if (deletedIds.size === 0) return nodeMap;

      return Object.fromEntries(
        Object.entries(nodeMap).filter(([id]) => !deletedIds.has(id)),
      ) as NodeMap;
    },
    addNodes: permissions.addNodes,
    removeNode: permissions.removeNode,
    setNodeFile: permissions.setNodeFile,
    replaceNodeFile: permissions.replaceNodeFile,
  });
  const addedNodeSummaries = useMemo(
    () =>
      isConfirmSaveOpen ? buildAddedNodeSummaries(permissions.nodeMap) : [],
    [isConfirmSaveOpen, permissions.nodeMap],
  );
  const deletedNodes = useMemo(
    () =>
      getPendingNodes(permissions.deletedIds, permissions.nodeMap, (node) => ({
        id: node.id,
        path: node.path ?? node.name,
        type: node.type,
      })),
    [permissions.deletedIds, permissions.nodeMap],
  );
  const replacedNodes = useMemo(
    () =>
      getPendingNodes(permissions.replacedIds, permissions.nodeMap, (node) => ({
        id: node.id,
        path: node.path ?? node.name,
      })),
    [permissions.replacedIds, permissions.nodeMap],
  );
  const {
    value: isRootDragOver,
    setTrue: setIsRootDragOver,
    setFalse: clearIsRootDragOver,
  } = useBoolean(false);
  const [rootDropEl, setRootDropEl] = useState<HTMLTableCellElement | null>(
    null,
  );
  const [deleteConfirmNodeId, setDeleteConfirmNodeId] = useState<string | null>(
    null,
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const frozenAnchorRef = useRef<HTMLTableCellElement>(null);
  useScrollFrozenColumnFocus(scrollContainerRef, frozenAnchorRef);

  // Use refs for upload.handleDrop and permissions.nodeMap so the drop effect
  // registration runs once per element mount without teardown mid-drag
  const handleDropRef = useRef(upload.handleDrop);
  handleDropRef.current = upload.handleDrop;
  const nodeMapRef = useRef(permissions.nodeMap);
  nodeMapRef.current = permissions.nodeMap;
  const {
    value: isAddDocumentOpen,
    setTrue: openAddDocument,
    setFalse: closeAddDocument,
  } = useBoolean(false);
  const {
    value: isDiscardConfirmOpen,
    setTrue: openDiscardConfirm,
    setFalse: closeDiscardConfirm,
  } = useBoolean(false);
  const [selectedPdfNode, setSelectedPdfNode] = useState<TreeNode | null>(null);
  const {
    data: pdfFileData,
    isLoading: isPdfUrlLoading,
    isError: isPdfUrlError,
    error: pdfUrlError,
  } = useGetDataRoomFile(
    dealId,
    dataRoomId ?? "",
    selectedPdfNode?.file?.id ?? "",
    { withDownloadUrl: true },
    { query: { enabled: !!(selectedPdfNode?.file?.id && dataRoomId) } },
  );

  useEffect(() => {
    if (isPdfUrlError) {
      showErrorToast(pdfUrlError as Parameters<typeof showErrorToast>[0]);
    }
  }, [isPdfUrlError, pdfUrlError, showErrorToast]);

  function handleOpenFile(node: TreeNode): void {
    if (!node.file?.id || !dataRoomId) return;
    openDataRoomFile({
      role: "gp",
      dealId,
      dataRoomId,
      fileId: node.file.id,
      filename: node.name,
    }).catch((error) => {
      showErrorToast(error as Parameters<typeof showErrorToast>[0]);
    });
  }

  function handleOpenCreateFolder(parentId: string | null) {
    setModalParentId(parentId);
    openCreateFolder();
  }

  function handleOpenAddDocument(parentId: string | null) {
    setModalParentId(parentId);
    openAddDocument();
  }

  async function handleConfirmSave() {
    try {
      await permissions.handleSave();
      upload.reset();
      closeConfirmSave();
      showToast({
        title: m.coInvestments_dataRoom_savedChanges(),
        variant: "success",
      });
    } catch (error) {
      showErrorToast(error as Parameters<typeof showErrorToast>[0]);
    }
  }

  useEffect(() => {
    if (!rootDropEl || isMutationLocked) return;

    return combine(
      dropTargetForExternal({
        element: rootDropEl,
        canDrop: containsFiles,
        onDragEnter: () => setIsRootDragOver(),
        onDragLeave: () => clearIsRootDragOver(),
        onDrop: () => clearIsRootDragOver(),
      }),
      monitorForExternal({
        canMonitor: containsFiles,
        onDragStart: () => preventUnhandled.start(),
        onDrop: ({ source, location }) => {
          preventUnhandled.stop();
          const dropTargets = location.current.dropTargets;

          if (dropTargets.length === 0) return;

          const targetElement = dropTargets[0].element;
          const parentId = resolveParentId(
            targetElement,
            rootDropEl,
            nodeMapRef.current,
          );

          handleDropRef
            .current(source.items, parentId)
            .catch((error) =>
              showErrorToast(error as Parameters<typeof showErrorToast>[0]),
            );
        },
      }),
    );
  }, [rootDropEl, isMutationLocked, setIsRootDragOver, clearIsRootDragOver]);

  if (isLoading) {
    return (
      <div
        className="flex h-48 items-center justify-center"
        data-testid="data-room-loading"
      >
        <Spinner className="w-16 h-16" />
        <span className="sr-only">{m.coInvestments_dataRoom_loading()}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex h-48 items-center justify-center"
        data-testid="data-room-error"
      >
        <p className="body-base-regular text-text/secondary">
          {m.coInvestments_dataRoom_loadError()}
        </p>
      </div>
    );
  }

  return (
    <div
      className={cnTwMerge("w-full", className)}
      data-testid="data-room-permissions"
    >
      {isMutationLocked ? null : (
        <UnsavedChangesBar
          changeCount={permissions.changeCount}
          isSaving={permissions.isSaving}
          isUploadActive={upload.isUploadActive}
          onAddDocument={() => handleOpenAddDocument(null)}
          onCreateFolder={() => handleOpenCreateFolder(null)}
          onDiscard={openDiscardConfirm}
          onSave={openConfirmSave}
        />
      )}
      {upload.uploadState.items.length > 0 && hasChanges ? (
        <UploadProgressPanel
          onRemoveItem={upload.removeItem}
          onRetryFailed={upload.retryFailed}
          onRetrySingle={upload.retrySingle}
          onUnmount={upload.reset}
          rejectedFiles={upload.uploadState.rejectedFiles}
          uploadState={upload.uploadState}
        />
      ) : null}
      <div
        className={cnTwMerge(
          "isolate w-full overflow-x-auto overflow-y-visible md:max-h-[75dvh] md:overflow-y-auto rounded border border-border/primary",
          (treeColumn.isResizing || lpColumns.resizingId !== null) &&
            "select-none",
        )}
        ref={scrollContainerRef}
      >
        <table
          className="w-0 min-w-full border-separate border-spacing-0"
          data-testid="data-room-permissions-table"
          style={{ tableLayout: "fixed" }}
        >
          <colgroup>
            <col
              data-testid="col-tree"
              style={{ width: treeColumn.width, minWidth: treeColumn.width }}
            />
            <col
              style={{
                width: DOWNLOAD_COLUMN_WIDTH,
                minWidth: DOWNLOAD_COLUMN_WIDTH,
              }}
            />
            <col
              style={{ width: ALL_COLUMN_WIDTH, minWidth: ALL_COLUMN_WIDTH }}
            />
            {dealOpportunities.map((dealOpportunity) => (
              <col
                data-testid={`col-lp-${dealOpportunity.id}`}
                key={dealOpportunity.id}
                style={{
                  width:
                    lpColumns.widths[dealOpportunity.id] ??
                    LP_COLUMN_INITIAL_WIDTH,
                  minWidth: LP_COLUMN_MIN_WIDTH,
                }}
              />
            ))}
            <col />
          </colgroup>
          <thead>
            <tr>
              <th
                className="relative md:sticky left-0 md:top-0 md:z-50 border-b border-border/primary bg-background/primary p-0 text-left"
                scope="col"
                style={{
                  width: treeColumn.width,
                  minWidth: treeColumn.width,
                }}
              >
                <div className="overflow-x-clip px-2 py-1.5">
                  <span className="body-small-medium text-text/primary">
                    {m.coInvestments_dataRoom_documents()}
                  </span>
                </div>
                <div
                  aria-hidden="true"
                  className="group absolute right-0 top-0 z-30 flex h-full w-3 translate-x-1/2 cursor-col-resize items-center justify-center"
                  data-testid="tree-column-resize-handle"
                  onDoubleClick={treeColumn.handleDoubleClick}
                  onMouseDown={treeColumn.handleMouseDown}
                >
                  <div
                    className={cnTwMerge(
                      "h-full w-0.5 rounded-full transition-colors",
                      treeColumn.isResizing
                        ? "bg-brand-default"
                        : "bg-border/primary group-hover:bg-brand-hover",
                    )}
                  />
                </div>
              </th>
              <th
                aria-label={m.coInvestments_dataRoom_fileToggle_downloadable()}
                className="md:sticky md:top-0 md:z-50 border-b border-border/primary bg-background/primary px-1 py-1.5"
                data-testid="download-column-header"
                scope="col"
                style={{ left: treeColumn.width }}
              />
              <th
                aria-label={m.coInvestments_dataRoom_all()}
                className="md:sticky md:top-0 md:z-50 border-b border-border/primary bg-background/primary px-1 py-1.5"
                data-testid="all-column-header"
                ref={frozenAnchorRef}
                scope="col"
                style={{
                  left: treeColumn.width + DOWNLOAD_COLUMN_WIDTH,
                  borderRightWidth: ALL_COLUMN_BORDER_RIGHT_WIDTH,
                }}
              />
              {dealOpportunities.map((dealOpportunity) => (
                <LpColumnHeader
                  columnTriState={permissions.getTriStateForDealOpportunityColumn(
                    dealOpportunity.id,
                  )}
                  dealOpportunity={dealOpportunity}
                  isResizing={lpColumns.resizingId === dealOpportunity.id}
                  key={dealOpportunity.id}
                  onResizeDoubleClick={lpColumns.makeHandleDoubleClick(
                    dealOpportunity.id,
                  )}
                  onResizeStart={lpColumns.makeHandleMouseDown(
                    dealOpportunity.id,
                  )}
                  onToggleColumn={
                    permissions.visibleRows.length > 0 && !isMutationLocked
                      ? () =>
                          permissions.toggleDealOpportunityColumnAccess(
                            dealOpportunity.id,
                          )
                      : undefined
                  }
                  width={
                    lpColumns.widths[dealOpportunity.id] ??
                    LP_COLUMN_INITIAL_WIDTH
                  }
                />
              ))}
              {/* spacer column - due to the table taking up the full width of the container, this column grows to fill the remaining space */}
              <th
                aria-hidden="true"
                className="md:sticky md:top-0 md:z-30 border-b border-border/primary bg-background/primary"
              />
            </tr>
          </thead>
          <tbody>
            {permissions.visibleRows.map((node) => {
              const isFolder = node.type === "folder";
              const rowTriState = isFolder
                ? permissions.getTriStateForFolderRow(node.id)
                : permissions.getTriStateForFileRow(node.id);

              const handleRowToggle = () => {
                if (isFolder) {
                  permissions.toggleFolderRowAccess(node.id);
                } else {
                  permissions.toggleFileRowAccess(node.id);
                }
              };

              return (
                <tr
                  className={
                    isFolder
                      ? "bg-background/secondary"
                      : "bg-background/primary"
                  }
                  key={node.id}
                >
                  <FileTreeRow
                    addItemMenu={
                      isMutationLocked ? undefined : (
                        <NodeActionsMenu
                          nodeId={node.id}
                          nodeName={node.name}
                          onAddDocument={
                            isFolder
                              ? () => handleOpenAddDocument(node.id)
                              : undefined
                          }
                          onCreateFolder={
                            isFolder
                              ? () => handleOpenCreateFolder(node.id)
                              : undefined
                          }
                          onDelete={() => {
                            const hasVisibleChildren =
                              isFolder &&
                              node.children.some(
                                (id) => !permissions.deletedIds.has(id),
                              );

                            if (hasVisibleChildren) {
                              setDeleteConfirmNodeId(node.id);
                            } else {
                              permissions.deleteNode(node.id);
                            }
                          }}
                        />
                      )
                    }
                    depth={node.depth}
                    isCollapsed={permissions.collapsed.has(node.id)}
                    isReplaced={permissions.replacedIds.has(node.id)}
                    node={node}
                    onOpenFile={handleOpenFile}
                    onOpenPdf={setSelectedPdfNode}
                    onToggleCollapse={() => permissions.toggleCollapse(node.id)}
                  />
                  <DataRoomRowBulkActions
                    isDownloadable={permissions.isDownloadable(node.id)}
                    isFolder={isFolder}
                    nodeId={node.id}
                    nodeName={node.name}
                    onRowToggle={
                      dealOpportunities.length > 0 && !isMutationLocked
                        ? handleRowToggle
                        : undefined
                    }
                    onToggleDownload={
                      isMutationLocked || (!isFolder && !isViewableInApp(node))
                        ? undefined
                        : () => permissions.toggleDownloadable(node.id)
                    }
                    rowTriState={rowTriState}
                    treeColumnWidth={treeColumn.width}
                  />
                  {dealOpportunities.map((dealOpportunity) => (
                    <DataRoomLpAccessCell
                      accessForNode={permissions.access[node.id]}
                      dealOpportunityId={dealOpportunity.id}
                      getTriStateForFolderDealOpportunity={
                        permissions.getTriStateForFolderDealOpportunity
                      }
                      isFolder={isFolder}
                      key={dealOpportunity.id}
                      lpName={dealOpportunity.limitedPartner.name}
                      nodeId={node.id}
                      nodeName={node.name}
                      readOnly={isMutationLocked}
                      setFileDealOpportunityAccess={
                        permissions.setFileDealOpportunityAccess
                      }
                      toggleFolderDealOpportunityAccess={
                        permissions.toggleFolderDealOpportunityAccess
                      }
                    />
                  ))}
                  <td
                    className={cnTwMerge(
                      "h-9 border-b border-border/primary",
                      isFolder
                        ? "bg-background/secondary"
                        : "bg-background/primary",
                    )}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <DataRoomDropZone
        hasProspects={dealOpportunities.length > 0}
        isEmpty={permissions.visibleRows.length === 0}
        isRootDragOver={isRootDragOver}
        readOnly={isMutationLocked}
        setRootDropEl={setRootDropEl}
      />
      {isAddDocumentOpen ? (
        <AddDocumentModal
          initialParentId={modalParentId}
          nodeMap={permissions.nodeMap}
          onSubmit={(file, parentId) => {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            upload.handleFilePick(dataTransfer.files, parentId);
          }}
          open
          setOpen={(val) => {
            if (!val) closeAddDocument();
          }}
        />
      ) : null}
      {isCreateFolderOpen ? (
        <CreateFolderModal
          initialParentId={modalParentId}
          nodeMap={permissions.nodeMap}
          onSubmit={permissions.createFolder}
          open
          setOpen={(val) => {
            if (!val) closeCreateFolder();
          }}
        />
      ) : null}
      {isConfirmSaveOpen ? (
        <PendingChangesModal
          addedNodeSummaries={addedNodeSummaries}
          changes={permissions.diff}
          dealOpportunities={dealOpportunities}
          deletedNodes={deletedNodes}
          isSaving={permissions.isSaving}
          onConfirm={handleConfirmSave}
          open
          replacedNodes={replacedNodes}
          setOpen={(val) => {
            if (!val) closeConfirmSave();
          }}
        />
      ) : null}
      {upload.pendingConflicts !== null ? (
        <ReplaceFilesModal
          conflicts={upload.pendingConflicts}
          onCancel={() => upload.resolvePendingConflicts("cancel")}
          onKeepBoth={() => upload.resolvePendingConflicts("keep-both")}
          onReplace={() => upload.resolvePendingConflicts("replace")}
          open
        />
      ) : null}
      <ActionaryModal
        ModalButtonsGroupProps={{
          mainAction: () => {
            if (deleteConfirmNodeId) {
              permissions.deleteNode(deleteConfirmNodeId);
            }
            setDeleteConfirmNodeId(null);
          },
          mainActionText: m.coInvestments_dataRoom_delete(),
          mainActionVariant: "destructive",
          secondaryAction: () => setDeleteConfirmNodeId(null),
          secondaryActionText: m.common_cancel(),
        }}
        dataTestId="delete-folder-confirm-modal"
        description={m.coInvestments_dataRoom_deleteFolderConfirm()}
        fluidHeight
        open={deleteConfirmNodeId !== null}
        setOpen={(val) => {
          if (!val) setDeleteConfirmNodeId(null);
        }}
        title={m.coInvestments_dataRoom_delete()}
      />
      <ActionaryModal
        ModalButtonsGroupProps={{
          mainAction: () => {
            permissions.handleDiscard();
            upload.reset();
            closeDiscardConfirm();
          },
          mainActionText: m.coInvestments_dataRoom_discard(),
          mainActionVariant: "destructive",
          secondaryAction: closeDiscardConfirm,
          secondaryActionText: m.common_cancel(),
        }}
        dataTestId="discard-confirm-modal"
        description={m.coInvestments_dataRoom_discardConfirm()}
        fluidHeight
        open={isDiscardConfirmOpen}
        setOpen={(val) => {
          if (!val) closeDiscardConfirm();
        }}
        title={m.coInvestments_dataRoom_discard()}
      />
      <UnsavedChangesModal
        cancel={navigationBlocker.cancel}
        isBlocked={navigationBlocker.isBlocked}
        proceed={navigationBlocker.proceed}
      />
      {selectedPdfNode !== null ? (
        <PDFViewer
          blobOrString={pdfFileData?.data.file.url}
          downloadFileName={selectedPdfNode.name}
          isDownloadEnabled
          isLoading={isPdfUrlLoading}
          isOpen
          setIsOpen={(open) => {
            if (!open) setSelectedPdfNode(null);
          }}
        />
      ) : null}
    </div>
  );
}
